import type { CMYKColor, Distance, Margins, Rectangle } from "@pdfking/math"
import { Max, Total } from "@pdfking/math"
import { ZERO_MARGINS } from "@pdfking/math/constants"
import type { PageNumber, PrintLayer, RequestRaster } from "@pdfking/renderer-api"
import type { FileName } from "@pdfking/structs"
import type { ContentRenderer } from "@pdfking/templates"
import { GrowContentKind } from "@pdfking/templates"
import { CalendarYear, CssPalette } from "@pdfking/templates-content"
import type { ContentPiece, ContentRequest } from "@pdfking/templates-content"
import type { StringGUID } from "@pdfking/tfunc"
import { AtomHash, GuidToString, JSONEncode, Range } from "@pdfking/tfunc"

import type { Book, BookTexture, PageSize } from "#public"

// A printed face: its pieces, the page it is in their numbering, the last
// page of it, what its block or cover draws it as and the security margins
// its text is set within
type Face = {
  page: PageNumber
  total: PageNumber
  pieces: ContentPiece[]
  renderer: ContentRenderer
  is_cover: boolean
  one_sided: boolean
  security: Margins<Distance>
}

type Layer = PrintLayer["enum"]

// The last page the pieces are numbered to
const last_of = (pieces: ContentPiece[]): PageNumber =>
  Max(...pieces.flatMap(piece => [piece.page_front ?? 0, piece.page_rear ?? 0])) as PageNumber

// What each printed face shows, in the order the viewer numbers them (it only
// counts the printed ones). Every face is placed by the side and sheet it is
// on, printed or not, so an unprinted face shifts nothing. A cover's faces
// are its pieces, numbered as the cover's grow numbers them; a grown block's
// its own, numbered the same way. A plain page is one piece over it, its
// side's number among the blocks'; each face is a cover's only when its grow
// is one
const faces_of = (book: Book): Face[] => {
  const faces: Face[] = []
  const { cover } = book
  if (cover) {
    const { is_cover, pieces, renderer, security } = cover
    const total = last_of(pieces)
    const of = (page: number): Face => ({
      is_cover,
      one_sided: !cover.print_rear,
      page: page as PageNumber,
      pieces,
      renderer,
      security,
      total,
    })
    if (cover.print_front) faces.push(of(1))
    if (cover.print_rear) faces.push(of(2))
  }
  const total = (2 * Total(...book.blocks.map(block => block.sheets))) as PageNumber
  const whole = (block: Book["blocks"][number], page: number): ContentPiece => {
    const is_front = page % 2 === 1
    return {
      enum: GrowContentKind.PAGE,
      box: { ...block.size, x: 0 as Distance, y: 0 as Distance },
      flip: undefined,
      label: undefined,
      page_front: (is_front ? page : page - 1) as PageNumber,
      page_rear: (is_front ? page + 1 : page) as PageNumber,
      rotation: 0,
    }
  }
  const face = (block: Book["blocks"][number], side: number): Face =>
    block.pieces.length > 0
      ? {
          is_cover: block.is_cover,
          one_sided: !block.print_rear,
          page: side as PageNumber,
          pieces: block.pieces,
          renderer: block.renderer,
          security: block.security,
          total: last_of(block.pieces),
        }
      : {
          is_cover: block.is_cover,
          one_sided: !block.print_rear,
          page: side as PageNumber,
          pieces: [whole(block, side)],
          renderer: block.renderer,
          security: block.security,
          total,
        }
  let side = 0
  for (const block of book.blocks)
    for (const _ of Range(1, block.sheets)) {
      if (block.print_front) faces.push(face(block, side + 1))
      if (block.print_rear) faces.push(face(block, side + 2))
      side += 2
    }
  return faces
}

// The destinations' inks, as imposition's JobDestinationList has them: the
// pantone's gold, the UV's gloss, the lamination's film
const PANTONE_COLOR = { alpha: 1, black: 0.2, cyan: 0, magenta: 0.25, yellow: 0.75 } as CMYKColor
const UV_COLOR = { alpha: 0.2, black: 0.5, cyan: 0, magenta: 0, yellow: 0 } as CMYKColor
const LAMINATION_COLOR = { alpha: 0.3, black: 0, cyan: 0.3, magenta: 0, yellow: 0 } as CMYKColor

// Generated content for a book with nothing to show: the lorem book `seed`
// makes with @pdfking/templates-content. On every page a print, its spot
// elements in pantone with a UV gloss over them, and the lamination over it
// all, named by the seed so another seed's pages are never reused. The
// generated textures ask their rasters by the layer they draw (see
// DynamicPageRaster): a request has a file name and a page, nothing else.
// The pantone and the UV share the spot plate, one raster for both
export const CreateDynamicTextures = (book: Book, seed: StringGUID): BookTexture[] => {
  // Another book, or another CSS palette, draws other rasters for the same
  // page: both are in the name, or the viewer would show the last ones from
  // its cache
  const shape = JSONEncode([book, CssPalette()])
  return faces_of(book)
    .map((_, i) => (i + 1) as PageNumber)
    .flatMap(page_number => {
      const layer = (layer: Layer, order: number) => ({
        filename: layer as string as FileName,
        guid: GuidToString(AtomHash([seed, shape, layer, page_number])),
        order,
        page: page_number,
      })
      return [
        {
          page_number,
          texture: {
            enum: "coating" as const,
            ...layer("coating", 3),
            print_color: LAMINATION_COLOR,
            print_roughness: 0.3,
          },
        },
        {
          page_number,
          texture: {
            enum: "overlay" as const,
            ...layer("overlay", 2),
            print_color: UV_COLOR,
            print_roughness: 0,
          },
        },
        {
          page_number,
          texture: {
            enum: "overlay" as const,
            ...layer("overlay", 1),
            print_color: PANTONE_COLOR,
            print_roughness: 1,
          },
        },
        { page_number, texture: { enum: "print" as const, ...layer("print", 0) } },
      ]
    })
}

/** What draws the book's page `page_number`, the lorem book `seed` makes:
 * its pieces stretched onto `size`, the page as it is cut, their grounds and
 * patterns running on into its `bleed`, in the page's own units, by its
 * block's or cover's renderer, its text within their security margins. In
 * the page's CSS palette, read here, as its `layer` draws: the print, an
 * overlay's decorations, or a coating's film, the same on every page; its
 * months and weekdays named in `language`, a calendar's or an agenda's for
 * the year one made now is for. Plain data, for
 * @pdfking/templates-content's TaskGenerateContent to draw wherever it runs. */
export const DynamicPageContent = (
  book: Book,
  seed: StringGUID,
  language: string,
  page_number: PageNumber,
  size: Rectangle<Distance>,
  layer: Layer,
  bleed: Margins<Distance> = ZERO_MARGINS
): ContentRequest | undefined => {
  const face = faces_of(book)[page_number - 1]
  if (!face) return undefined
  return {
    bleed,
    is_cover: face.is_cover,
    language,
    layer,
    one_sided: face.one_sided,
    opening_top: book.opening_axis === "y",
    page_number: face.page,
    palette: CssPalette(),
    pieces: FitPieces(face.pieces, size),
    renderer: face.renderer,
    security: face.security,
    seed,
    size,
    total_pages: face.total,
    year: CalendarYear(),
  }
}

// `pieces` scaled onto `size`: they tile the page they were laid out on, so
// their reach is that page
const FitPieces = (pieces: ContentPiece[], size: Rectangle<Distance>): ContentPiece[] => {
  const width = Max(...pieces.map(piece => piece.box.x + piece.box.width))
  const height = Max(...pieces.map(piece => piece.box.y + piece.box.height))
  const sx = size.width / width
  const sy = size.height / height
  return pieces.map(piece => ({
    ...piece,
    box: {
      x: (piece.box.x * sx) as Distance,
      y: (piece.box.y * sy) as Distance,
      width: (piece.box.width * sx) as Distance,
      height: (piece.box.height * sy) as Distance,
    },
  }))
}

// The rasters of CreateDynamicTextures, asked by the viewer for its page:
// each the url `generate` answers for its page's content, named in `language`
export const CreateDynamicRequestRaster =
  (
    seed: StringGUID,
    language: string,
    get_page_size: (page_number: PageNumber) => PageSize,
    get_book: () => Book,
    generate: (request: ContentRequest) => Promise<string | undefined>
  ): RequestRaster =>
  (filename, page_number) => {
    const size = get_page_size(page_number)
    const request =
      size &&
      DynamicPageContent(get_book(), seed, language, page_number, size, filename as string as Layer)
    return request ? generate(request) : Promise.resolve(undefined)
  }
